/*
 * @format
 */

import uuid from 'uuid/v4';
import moment from 'moment';

import {
  superDebounce,
  debouncedSendAndTimeout,
  debounceSendControl,
  startDeviceServiceRequest,
} from './common';
import * as selectors from '../controls-selectors';
import * as scheduleSelectors from '../selectors/schedule-selectors';
import * as otherSelectors from '../selectors';
import * as types from '../action-types';
import * as messages from '../web_socket_messages/messages';
import { showDemoUnavailable } from '../utils/demoMode';

import { MODES, MAX_SETPOINT, isValidMode } from '../utils/consts';

export const setSetpoint =
  (sendWsMessage, homeId, zoneId, setpoint, userCurrentDate) =>
  async (dispatch, getState) => {
    if (otherSelectors.isDemoMode(getState())) {
      return dispatch(demoSetSetpoint(homeId, zoneId, setpoint));
    }

    const requestId = uuid();
    const state = getState();

    const mode = selectors.getMode(state, homeId, zoneId);
    const deviceToken = otherSelectors.getDeviceToken(state);

    const isHpi = selectors.isHpi(state, homeId, zoneId);

    const holidayModeActive = selectors.isInHolidayMode(state, homeId, zoneId);

    const frostProtectionSetpoint = selectors.getFrostProtectionSetpoint(
      state,
      homeId,
      zoneId,
    );

    const deviceServiceEnabled = otherSelectors.isHaloDeviceServiceEnabled(
      state,
      homeId,
    );

    if (!isValidMode(mode)) {
      return;
    }

    if (mode === MODES.Off) {
      return;
    }

    if (holidayModeActive) {
      return;
    }

    if (setpoint < frostProtectionSetpoint || setpoint > 3000) {
      return;
    }

    dispatch({
      type: types.SET_SETPOINT_STARTED,
      homeId,
      zoneId,
      setpoint,
      requestId,
    });

    let message;
    let desiredMode;
    switch (mode) {
      case MODES.Auto:
      case MODES.OverrideNext: {
        const newMode = mode === MODES.Auto ? MODES.OverrideNext : mode;

        message = isHpi
          ? messages.hpiSetMode(
              homeId,
              zoneId,
              requestId,
              newMode,
              setpoint,
              undefined,
              deviceToken,
            )
          : messages.setMode(
              homeId,
              zoneId,
              requestId,
              newMode,
              setpoint,
              undefined,
              deviceToken,
            );

        desiredMode = newMode;

        if (newMode !== mode) {
          dispatch({
            type: types.SET_MODE_STARTED,
            homeId,
            zoneId,
            requestId,
            mode: newMode,
          });
        }
        break;
      }
      case MODES.Manual: {
        desiredMode = MODES.Manual;

        message = isHpi
          ? messages.hpiSetMode(
              homeId,
              zoneId,
              requestId,
              desiredMode,
              setpoint,
              undefined,
              deviceToken,
            )
          : messages.setMode(
              homeId,
              zoneId,
              requestId,
              desiredMode,
              setpoint,
              undefined,
              deviceToken,
            );

        break;
      }
      case MODES.OverridePlusHours: {
        const currentDate = moment(userCurrentDate);
        // getPlusHoursEndtime resolves the end-time for a live override from the
        // reported/local overrideEndtime, and for a not-yet-applied peer goal
        // from its holdDuration — so this stays a valid OverridePlusHours,
        // consistent with the mode shown to the user.
        const plusEndtime = moment(
          selectors.getPlusHoursEndtime(state, homeId, zoneId),
        );
        const holdDuration = moment.duration(plusEndtime.diff(currentDate));
        const setpointDuration = Math.ceil(holdDuration.asMinutes());
        desiredMode = MODES.OverridePlusHours;

        message = isHpi
          ? messages.hpiSetMode(
              homeId,
              zoneId,
              requestId,
              desiredMode,
              setpoint,
              setpointDuration,
              deviceToken,
            )
          : messages.setMode(
              homeId,
              zoneId,
              requestId,
              desiredMode,
              setpoint,
              setpointDuration,
              deviceToken,
            );

        break;
      }
      default:
        break;
    }

    if (deviceServiceEnabled) {
      const envelope = {
        action: isHpi ? 'hpiDesiredMode' : 'siDesiredMode',
        deviceToken,
        data: {
          [isHpi ? 'hpiId' : 'siId']: homeId,
          prtId: zoneId[0],
          endpointId: zoneId[1],
          requestId,
          mode: message.data.mode,
          setpoint: message.data.setpoint,
          holdDuration: message.data.holdDuration || undefined,
        },
      };

      dispatch(startDeviceServiceRequest(homeId, requestId, message));

      console.log('Sending message with device service enabled', envelope);
      await dispatch(
        debounceSendControl(
          sendWsMessage,
          envelope,
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            zoneId,
            requestId,
          },
          30000,
        ),
      );
    } else {
      await dispatch(
        superDebounce(
          sendWsMessage,
          message,
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            zoneId,
            requestId,
          },
          {
            mode: desiredMode,
            setpoint,
          },
          { debounceEnabled: true },
        ),
      );
    }
  };

export const incrementSetpoint =
  (sendWsMessage, homeId, zoneId) => (dispatch, getState) => {
    const now = new Date();
    const newSetpoint = selectors.getSetpoint(getState(), homeId, zoneId) + 50;
    if (MAX_SETPOINT >= newSetpoint) {
      dispatch(setSetpoint(sendWsMessage, homeId, zoneId, newSetpoint, now));
    }
  };

export const decrementSetpoint =
  (sendWsMessage, homeId, zoneId) => (dispatch, getState) => {
    const now = new Date();
    const newSetpoint = selectors.getSetpoint(getState(), homeId, zoneId) - 50;
    if (
      selectors.getFrostProtectionSetpoint(getState(), homeId, zoneId) <=
      newSetpoint
    ) {
      dispatch(setSetpoint(sendWsMessage, homeId, zoneId, newSetpoint, now));
    }
  };

// Demo mode has no device to talk to. Behave exactly like a real setpoint
// change (optimistic update) but without sending a websocket request - set the
// pending value, then pretend the API confirms it after ~1 second so the
// change spends a moment in the pending state before it sticks (a done request
// keeps its value and survives, since no live message arrives to clear it).
export const DEMO_SETPOINT_PENDING_MS = 1000;

const demoRequestId = (verb, homeId, zoneId) =>
  `demo-${verb}-${homeId}-${Array.isArray(zoneId) ? zoneId.join('-') : zoneId}`;

// Confirm a pending demo change after ~1s: show the header "updating" (wave)
// indicator, then mark the request's pending setpoint and mode entries as done
// (confirming a request id with no matching entry is a no-op in the pending
// reducer), commit the confirmed values onto the reported zone state (as a
// live message from the device would - keeping reported-data selectors like
// the heating flame indicator in sync) and clear the wave once no other
// change for the home is still pending. A repeat change with the same request
// id (e.g. the radial dial firing per drag movement) replaces its timer
// rather than stacking a new one, so the wave stays on steadily until ~1s
// after the last adjustment.
const demoConfirmTimers = {};
const demoPendingCountByHome = {};

// Cancel any in-flight demo confirmations and reset the bookkeeping. Called
// on logout so a timer armed just before exiting demo can't fire into the
// next session (the demo home ids are fixed, so a stale timer would otherwise
// commit the previous session's values onto a freshly seeded zone).
export const clearDemoConfirmState = () => {
  Object.keys(demoConfirmTimers).forEach(key => {
    clearTimeout(demoConfirmTimers[key]);
    delete demoConfirmTimers[key];
  });
  Object.keys(demoPendingCountByHome).forEach(key => {
    delete demoPendingCountByHome[key];
  });
};

const demoConfirm = (dispatch, homeId, requestId, zoneId, reported) => {
  dispatch({ type: types.DEMO_CHANGE_TRACKER, homeId, waiting: true });

  if (demoConfirmTimers[requestId]) {
    clearTimeout(demoConfirmTimers[requestId]);
  } else {
    demoPendingCountByHome[homeId] = (demoPendingCountByHome[homeId] || 0) + 1;
  }

  demoConfirmTimers[requestId] = setTimeout(() => {
    delete demoConfirmTimers[requestId];
    demoPendingCountByHome[homeId] -= 1;

    dispatch({ type: types.DEMO_ZONE_REPORTED, homeId, zoneId, ...reported });
    dispatch({
      type: types.WEBSOCKET_SET_SETPOINT_RECEIVED,
      data: { requestId },
    });
    dispatch({
      type: types.WEBSOCKET_SET_PRT_MODE_RECEIVED,
      data: { requestId },
    });

    if (demoPendingCountByHome[homeId] === 0) {
      dispatch({ type: types.DEMO_CHANGE_TRACKER, homeId, waiting: false });
    }
  }, DEMO_SETPOINT_PENDING_MS);
};

export const demoSetSetpoint =
  (homeId, zoneId, setpoint) => (dispatch, getState) => {
    const requestId = demoRequestId('setpoint', homeId, zoneId);

    // Like the real setSetpoint: changing the setpoint while in Auto puts the
    // zone into OverrideNext (hold until the next scheduled event); other modes
    // are left unchanged.
    const mode = selectors.getMode(getState(), homeId, zoneId);
    const newMode = mode === MODES.Auto ? MODES.OverrideNext : mode;

    dispatch({
      type: types.SET_SETPOINT_STARTED,
      homeId,
      zoneId,
      setpoint,
      requestId,
    });
    if (newMode !== mode) {
      dispatch({
        type: types.SET_MODE_STARTED,
        homeId,
        zoneId,
        requestId,
        mode: newMode,
        previousMode: mode,
      });
    }

    demoConfirm(dispatch, homeId, requestId, zoneId, {
      setpoint,
      mode: newMode,
    });
  };

// Demo equivalent of setMode: mirrors the optimistic pending state a real mode
// change sets (mode, plus the accompanying setpoint for Auto/Off), shows the
// pending "wave" for ~1s, then pretends the API confirmed it - all without
// sending a websocket request.
export const demoSetMode = (homeId, zoneId, mode) => (dispatch, getState) => {
  const state = getState();
  const requestId = demoRequestId('mode', homeId, zoneId);
  const isDhw = zoneId[1] === 6;

  dispatch({
    type: types.SET_MODE_STARTED,
    homeId,
    zoneId,
    requestId,
    mode,
    previousMode: selectors.getMode(state, homeId, zoneId),
  });

  // Some mode changes carry an optimistic setpoint, matching setMode. The
  // reported setpoint is what the device would settle on for the new mode.
  let reportedSetpoint;
  if (mode === MODES.Off && !isDhw) {
    reportedSetpoint = selectors.getFrostProtectionSetpoint(
      state,
      homeId,
      zoneId,
    );
    dispatch({ type: types.SET_SETPOINT_STARTED, homeId, zoneId, requestId });
  } else if (mode === MODES.Auto && !isDhw) {
    reportedSetpoint = scheduleSelectors.getCurrentScheduleSetpoint(
      state,
      homeId,
      zoneId,
      new Date(),
    );
    dispatch({
      type: types.SET_SETPOINT_STARTED,
      homeId,
      zoneId,
      requestId,
      setpoint: reportedSetpoint,
    });
  }

  demoConfirm(dispatch, homeId, requestId, zoneId, {
    mode,
    ...(typeof reportedSetpoint === 'number' && {
      setpoint: reportedSetpoint,
    }),
  });
};

// Demo equivalent of cancelOverride (used to remove plus hours in demo):
// mirrors the optimistic pending state a real override-cancel sets (returns to
// the previous/Auto mode, with the matching setpoint for heating zones), shows
// the pending "wave" for ~1s, then pretends the API confirmed it - without
// sending a websocket request.
export const demoCancelOverride = (homeId, zoneId) => (dispatch, getState) => {
  const state = getState();
  const mode = selectors.getMode(state, homeId, zoneId);

  const supportedModes = [
    MODES.OverrideNext,
    MODES.OverridePlusHours,
    MODES.DSRActive,
  ];

  if (supportedModes.indexOf(mode) === -1) {
    return;
  }

  const requestId = demoRequestId('cancel', homeId, zoneId);
  const previousMode = selectors.getPreviousMode(state, homeId, zoneId);
  const isModeDsrActive = mode === MODES.DSRActive;
  const isDhw = zoneId[1] === 6;

  let newMode = isModeDsrActive ? previousMode : MODES.Auto;
  if (isDhw && previousMode === MODES.Off) {
    newMode = MODES.Off;
  }

  dispatch({
    type: types.SET_MODE_STARTED,
    homeId,
    zoneId,
    requestId,
    mode: newMode,
  });

  let reportedSetpoint;
  if (!isDhw) {
    reportedSetpoint =
      isModeDsrActive && newMode === MODES.Manual
        ? selectors.getComfortSetpoint(state, homeId, zoneId)
        : scheduleSelectors.getCurrentScheduleSetpoint(
            state,
            homeId,
            zoneId,
            new Date(),
          );
    dispatch({
      type: types.SET_SETPOINT_STARTED,
      homeId,
      zoneId,
      requestId,
      setpoint: reportedSetpoint,
    });
  }

  demoConfirm(dispatch, homeId, requestId, zoneId, {
    mode: newMode,
    overrideEndtime: '',
    ...(typeof reportedSetpoint === 'number' && {
      setpoint: reportedSetpoint,
    }),
  });
};

export const demoCancelOverridesForHome = homeId => (dispatch, getState) => {
  const zones = otherSelectors.getZones(getState(), homeId);

  for (let z of zones) {
    dispatch(demoCancelOverride(homeId, z.zoneId));
  }
};

// Demo equivalent of setPlusHours: sets the optimistic pending
// OverridePlusHours mode (with its end time and setpoint), shows the pending
// "wave" for ~1s, then pretends the API confirmed it - without sending a
// websocket request.
export const demoSetPlusHours =
  (homeId, zoneId, holdDuration) => (dispatch, getState) => {
    const state = getState();
    const mode = selectors.getMode(state, homeId, zoneId);
    const setpoint = selectors.getSetpoint(state, homeId, zoneId);

    const supportedModes = [
      MODES.Auto,
      MODES.OverrideNext,
      MODES.OverridePlusHours,
    ];

    if (supportedModes.indexOf(mode) === -1 && zoneId[1] === 5) {
      return;
    }

    const requestId = demoRequestId('plushours', homeId, zoneId);
    const overrideEndtime = moment().add(holdDuration, 'm').toISOString();

    dispatch({
      type: types.SET_MODE_STARTED,
      homeId,
      zoneId,
      requestId,
      mode: MODES.OverridePlusHours,
      overrideEndtime,
      previousMode: mode,
    });
    dispatch({
      type: types.SET_SETPOINT_STARTED,
      homeId,
      zoneId,
      setpoint,
      requestId,
    });

    demoConfirm(dispatch, homeId, requestId, zoneId, {
      mode: MODES.OverridePlusHours,
      overrideEndtime,
      ...(typeof setpoint === 'number' && { setpoint }),
    });
  };

export const setMode =
  (sendWsMessage, homeId, zoneId, mode) => (dispatch, getState) => {
    if (otherSelectors.isDemoMode(getState())) {
      return dispatch(demoSetMode(homeId, zoneId, mode));
    }

    const requestId = uuid();

    const state = getState();

    // device token is sent with the websocket message
    const deviceToken = otherSelectors.getDeviceToken(state);

    const deviceServiceEnabled = otherSelectors.isHaloDeviceServiceEnabled(
      state,
      homeId,
    );
    const holidayModeActive = selectors.isInHolidayMode(state, homeId, zoneId);

    // this is very unlikely to have happened, but worth protecting against. The PRT might not like it much
    if (holidayModeActive) {
      return;
    }

    // 5 is central heating, 6 is hot water. This is the case for SI and HPI PRTs.
    const isDhw = zoneId[1] === 6;
    const isHpi = selectors.isHpi(state, homeId, zoneId);

    // this stores the pending state, where it will be used to display to the users (and the loading spinner)
    // it will be cleared by the controls Saga later
    dispatch({
      type: types.SET_MODE_STARTED,
      homeId,
      zoneId,
      requestId,
      mode,
      // Base mode (own pending over reported), NOT the shared-desired overlay:
      // previousMode is the restore target for a DSR cancel, which must be the
      // real prior mode, never a peer's in-flight goal.
      previousMode: selectors.getBaseMode(state, homeId, zoneId),
    });

    // some mode changes require a setpoint to go with them, but not all
    let setpoint;

    if (mode === MODES.Manual) {
      // a manual mode needs the current setpoint to be sent otherwise the PRT will pick
      // one and the radial dial might get jumpy
      setpoint = selectors.getSetpoint(state, homeId, zoneId);
    } else if (mode === MODES.Off && !isDhw) {
      // off really is frost protection, so add that to the pending setpoint state
      // that will be cleared by the controls saga
      setpoint = selectors.getFrostProtectionSetpoint(state, homeId, zoneId);
      dispatch({
        type: types.SET_SETPOINT_STARTED,
        homeId,
        zoneId,
        requestId,
      });
    } else if (mode === MODES.Auto && !isDhw) {
      // locally set the pending setpoint to scheduled value
      // then wait for the Controls saga to clear it
      setpoint = scheduleSelectors.getCurrentScheduleSetpoint(
        state,
        homeId,
        zoneId,
        new Date(),
      );

      dispatch({
        type: types.SET_SETPOINT_STARTED,
        homeId,
        zoneId,
        requestId,
        setpoint,
      });
    }

    const message = isHpi
      ? messages.hpiSetMode(
          homeId,
          zoneId,
          requestId,
          mode,
          setpoint,
          null,
          deviceToken,
        )
      : messages.setMode(
          homeId,
          zoneId,
          requestId,
          mode,
          setpoint,
          null,
          deviceToken,
        );

    if (deviceServiceEnabled) {
      const envelope = {
        action: isHpi ? 'hpiDesiredMode' : 'siDesiredMode',
        deviceToken,
        data: {
          // action: 'desiredState', provided by api
          [isHpi ? 'hpiId' : 'siId']: homeId,
          prtId: zoneId[0],
          endpointId: zoneId[1],
          requestId,
          mode,
          setpoint: mode === MODES.Manual ? setpoint : undefined,
        },
      };

      dispatch(startDeviceServiceRequest(homeId, requestId, message));
      console.log('Sending message with device service enabled', envelope);
      dispatch(
        debounceSendControl(
          sendWsMessage,
          envelope,
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            zoneId,
            requestId,
          },
          30000,
        ),
      );
    } else {
      // this will fire the superDebounce output so it'll get handled somewhere else,
      dispatch(
        // don't actually send the command, but ship off the message body to be sent if it survives
        // multiple rounds of debouncing
        superDebounce(
          sendWsMessage,
          message,
          // this is the action that'll be fired if the timeout happens
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            zoneId,
            requestId,
          },
          // this is the state we expect the device to report before we'll say this command succeeded
          {
            mode,
            setpoint,
          },
        ),
      );
    }
  };

export const setPlusHours =
  (sendWsMessage, homeId, zoneId, holdDuration) =>
  async (dispatch, getState) => {
    if (otherSelectors.isDemoMode(getState())) {
      return dispatch(demoSetPlusHours(homeId, zoneId, holdDuration));
    }

    const requestId = uuid();

    const state = getState();

    const mode = selectors.getMode(state, homeId, zoneId);
    const setpoint = selectors.getSetpoint(state, homeId, zoneId);
    const deviceToken = otherSelectors.getDeviceToken(state);

    const deviceServiceEnabled = otherSelectors.isHaloDeviceServiceEnabled(
      state,
      homeId,
    );

    const supportedModes = [
      MODES.Auto,
      MODES.OverrideNext,
      MODES.OverridePlusHours,
    ];

    if (supportedModes.indexOf(mode) === -1 && zoneId[1] === 5) {
      return;
    }

    // set the pending change
    dispatch({
      type: types.SET_MODE_STARTED,
      homeId,
      zoneId,
      requestId,
      mode: MODES.OverridePlusHours,
      overrideEndtime: moment().add(holdDuration, 'm').toISOString(),
      // Base mode (own pending over reported), NOT the shared-desired overlay —
      // same reason as setMode: previousMode is the cancel/restore target and
      // must be the real prior mode, never a peer's in-flight goal.
      previousMode: selectors.getBaseMode(state, homeId, zoneId),
    });

    dispatch({
      type: types.SET_SETPOINT_STARTED,
      homeId,
      zoneId,
      setpoint,
      requestId,
    });

    const isHpi = selectors.isHpi(state, homeId, zoneId);

    // prevent a direct cancel when using the device service
    if (!deviceServiceEnabled) {
      // if going from overrideNext to overridePlus we need
      // to cancel the overrideNext or the PRT will crash
      if (mode === MODES.OverrideNext) {
        const cancelOverrideRequestId = uuid();
        const cancelOverrideMessage = isHpi
          ? messages.hpiCancelOverride(
              homeId,
              zoneId,
              cancelOverrideRequestId,
              deviceToken,
            )
          : messages.cancelOverride(
              homeId,
              zoneId,
              cancelOverrideRequestId,
              deviceToken,
            );

        await dispatch(
          superDebounce(
            sendWsMessage,
            cancelOverrideMessage,
            {
              type: types.PENDING_CHANGE_TIMED_OUT,
              homeId,
              zoneId,
              requestId: cancelOverrideRequestId,
            },
            {
              mode: MODES.Auto,
              setpoint,
            },
          ),
        );
      }
    }

    const message = isHpi
      ? messages.hpiSetMode(
          homeId,
          zoneId,
          requestId,
          MODES.OverridePlusHours,
          setpoint,
          holdDuration,
          deviceToken,
        )
      : messages.setMode(
          homeId,
          zoneId,
          requestId,
          MODES.OverridePlusHours,
          setpoint,
          holdDuration,
          deviceToken,
        );

    if (deviceServiceEnabled) {
      const envelope = {
        action: isHpi ? 'hpiDesiredMode' : 'siDesiredMode',
        deviceToken,
        data: {
          [isHpi ? 'hpiId' : 'siId']: homeId,
          prtId: zoneId[0],
          endpointId: zoneId[1],
          requestId,
          mode: message.data.mode,
          setpoint: message.data.setpoint || undefined,
          holdDuration: message.data.holdDuration || undefined,
          deviceToken,
        },
      };

      dispatch(startDeviceServiceRequest(homeId, requestId, message));
      console.log('Sending message with device service enabled', envelope);
      await dispatch(
        debounceSendControl(
          sendWsMessage,
          envelope,
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            zoneId,
            requestId,
          },
          30000,
        ),
      );
    } else {
      await dispatch(
        superDebounce(
          sendWsMessage,
          message,
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            zoneId,
            requestId,
          },
          {
            mode: MODES.OverridePlusHours,
            setpoint,
          },
        ),
      );
    }
  };

export const cancelOverridesForHome =
  (sendWsMessage, homeId) => async (dispatch, getState) => {
    if (otherSelectors.isDemoMode(getState())) {
      return dispatch(demoCancelOverridesForHome(homeId));
    }

    const state = getState();
    const zones = otherSelectors.getZones(state, homeId);

    for (let z of zones) {
      dispatch(cancelOverride(sendWsMessage, homeId, z.zoneId));
    }
  };

export const cancelOverride =
  (sendWsMessage, homeId, zoneId) => async (dispatch, getState) => {
    if (otherSelectors.isDemoMode(getState())) {
      return dispatch(demoCancelOverride(homeId, zoneId));
    }

    const requestId = uuid();

    const state = getState();

    const previousMode = selectors.getPreviousMode(state, homeId, zoneId);
    const mode = selectors.getMode(state, homeId, zoneId);
    const deviceToken = otherSelectors.getDeviceToken(state);
    const isModeDsrActive = mode === MODES.DSRActive;

    const supportedModes = [
      MODES.OverrideNext,
      MODES.OverridePlusHours,
      MODES.DSRActive,
    ];

    const deviceServiceEnabled = otherSelectors.isHaloDeviceServiceEnabled(
      state,
      homeId,
    );

    if (supportedModes.indexOf(mode) === -1) {
      return;
    }

    const isDhw = zoneId[1] === 6;

    let newMode = isModeDsrActive ? previousMode : MODES.Auto;
    const setpoint =
      isModeDsrActive && newMode === MODES.Manual
        ? selectors.getComfortSetpoint(state, homeId, zoneId)
        : scheduleSelectors.getCurrentScheduleSetpoint(
            state,
            homeId,
            zoneId,
            new Date(),
          );

    if (isDhw) {
      if (previousMode === MODES.Off) {
        newMode = MODES.Off;
      }
      dispatch({
        type: types.SET_MODE_STARTED,
        homeId,
        zoneId,
        requestId,
        mode: newMode,
      });
    } else {
      // set the pending change
      dispatch({
        type: types.SET_MODE_STARTED,
        homeId,
        zoneId,
        requestId,
        mode: newMode,
      });
      dispatch({
        type: types.SET_SETPOINT_STARTED,
        homeId,
        zoneId,
        requestId,
        setpoint,
      });
    }

    const isHpi = selectors.isHpi(state, homeId, zoneId);

    if (deviceServiceEnabled) {
      let message = {
        action: isHpi ? 'hpiDesiredMode' : 'siDesiredMode',
        deviceToken,
        data: {
          [isHpi ? 'hpiId' : 'siId']: homeId,
          prtId: zoneId[0],
          endpointId: zoneId[1],
          requestId,
          mode: newMode,
          setpoint: undefined,
        },
      };

      if (isModeDsrActive) {
        message = {
          action: 'hpiCancelDsrActive',
          deviceToken,
          data: {
            hpiId: homeId,
            prtId: zoneId[0],
            requestId,
          },
        };
      }

      dispatch(startDeviceServiceRequest(homeId, requestId, {}));
      await dispatch(
        debounceSendControl(
          sendWsMessage,
          message,
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            zoneId,
            requestId,
            suppressTimeoutError: true,
          },
          30000,
        ),
      );
    } else {
      const message = isHpi
        ? messages.hpiCancelOverride(homeId, zoneId, requestId, deviceToken)
        : messages.cancelOverride(homeId, zoneId, requestId, deviceToken);

      await dispatch(
        superDebounce(
          sendWsMessage,
          message,
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            zoneId,
            requestId,
          },
          {
            mode: isModeDsrActive ? previousMode : MODES.Auto,
            setpoint,
          },
        ),
      );
    }
  };

// The date picker hands us a Date, but an end time has to be an ISO string by
// the time it reaches the store: a Date trips redux-toolkit's serializability
// check, and this value sits in state next to the reported holidayModeEnd
// telemetry, which is a string — keeping one shape means every reader can treat
// them alike. Ordering is not this function's job: getNearestHolidayEndTime
// compares instants rather than spellings, so it copes with either.
//
// The wire payload is unaffected, since sendWsMessage JSON.stringifies the
// message to this same string anyway.
const toHolidayEndTime = endTime => {
  if (endTime == null || endTime === '') {
    return endTime;
  }

  const parsed = moment(endTime);

  // Date, moment, epoch or ISO string all normalise to the one shape. Anything
  // moment cannot read passes through untouched rather than becoming the null
  // that toISOString() returns for an invalid moment.
  return parsed.isValid() ? parsed.toISOString() : endTime;
};

export const setHoliday =
  (sendWsMessage, homeId, zoneId, endTime) =>
  async (dispatch, getState) => {
    const requestId = uuid();
    const holidayEndTime = toHolidayEndTime(endTime);

    const state = getState();
    const deviceToken = otherSelectors.getDeviceToken(state);

    const isHpi = selectors.isHpi(state, homeId, zoneId);

    dispatch({
      type: types.SET_HOLIDAY_MODE_STARTED,
      homeId,
      zoneId,
      requestId,
      holidayMode: 3,
      // one of these is in the request we send and the other is the reported
      // value, so set both to be sure (matching the device-service path). Without
      // holidayModeEnd the optimistic state has holidayMode:3 but
      // getNearestHolidayEndTime (which reads holidayModeEnd) stays undefined, so
      // an "away/until" view does not reflect the change until the device acks.
      holidayEndTime,
      holidayModeEnd: holidayEndTime,
    });

    const dhwZones = otherSelectors
      .getZones(state, homeId)
      .filter(x => x.zoneId[0] === zoneId[0] && x.zoneId[1] === 6);

    // setup a the pending data for the dhw zone but don't send a command
    if (dhwZones && dhwZones.length > 0) {
      dispatch({
        type: types.SET_HOLIDAY_MODE_STARTED,
        homeId,
        zoneId: dhwZones[0].zoneId,
        requestId: uuid(),
        holidayMode: 3,
        holidayEndTime,
        holidayModeEnd: holidayEndTime,
      });
    }

    const message = isHpi
      ? messages.hpiSetHolidayMode(
          homeId,
          zoneId,
          requestId,
          holidayEndTime,
          deviceToken,
        )
      : messages.setHolidayMode(
          homeId,
          zoneId,
          requestId,
          holidayEndTime,
          deviceToken,
        );

    await dispatch(
      superDebounce(
        sendWsMessage,
        message,
        {
          type: types.PENDING_CHANGE_TIMED_OUT,
          homeId,
          zoneId,
          requestId,
        },
        {
          holidayMode: 3,
        },
        {
          debounceEnabled: false,
          timeoutOverride: 45000,
        },
      ),
    );
  };

export const startHolidayAllZones =
  (sendWsMessage, homeId, zoneIds, endTime) => async (dispatch, getState) => {
    if (otherSelectors.isDemoMode(getState())) {
      dispatch(showDemoUnavailable());
      return;
    }

    const holidayEndTime = toHolidayEndTime(endTime);

    const state = getState();
    const deviceServiceEnabled = otherSelectors.isHaloDeviceServiceEnabled(
      state,
      homeId,
    );

    const zones = otherSelectors.getZones(state, homeId);

    const isHpi = selectors.isHpi(state, homeId, zoneIds[0]);
    if (deviceServiceEnabled) {
      const requestId = uuid();
      // mark all zones as in holiday to prevent changes
      zones.map(z => {
        dispatch({
          type: types.SET_HOLIDAY_MODE_STARTED,
          homeId,
          zoneId: z.zoneId,
          requestId,
          holidayMode: 3,
          // one of these is in the request we send and the other is the reported value, so set both to be sure
          holidayEndTime,
          holidayModeEnd: holidayEndTime,
        });
      });

      const deviceToken = otherSelectors.getDeviceToken(state);

      dispatch(startDeviceServiceRequest(homeId, requestId, {}));

      await dispatch(
        debounceSendControl(
          sendWsMessage,
          {
            action: isHpi ? 'hpiDesiredHolidayMode' : 'siDesiredHolidayMode',
            deviceToken,
            data: {
              [isHpi ? 'hpiId' : 'siId']: homeId,
              prtIds: zoneIds.map(z => z[0]),
              requestId,
              intoHoliday: true,
              holidayEndTime,
            },
          },
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            zoneId: zoneIds[0],
            requestId,
            suppressTimeoutError: true,
          },
          30000,
        ),
      );
    } else {
      for (const id of zoneIds) {
        // we can safely dispatch cancelOverride as it'll check the if it should
        // send the message before it sends
        // both holiday mode and cancel override go through the controls saga
        // so will be queued nicely
        dispatch(cancelOverride(sendWsMessage, homeId, id));
        dispatch(setHoliday(sendWsMessage, homeId, id, holidayEndTime));
      }
    }
  };

export const cancelHolidayAllZones =
  (sendWsMessage, homeId) => async (dispatch, getState) => {
    if (otherSelectors.isDemoMode(getState())) {
      dispatch(showDemoUnavailable());
      return;
    }

    const state = getState();
    const deviceToken = otherSelectors.getDeviceToken(state);

    const zones = otherSelectors.getZones(state, homeId);

    const deviceServiceEnabled = otherSelectors.isHaloDeviceServiceEnabled(
      state,
      homeId,
    );

    if (deviceServiceEnabled) {
      const requestId = uuid();
      const isHpi = selectors.isHpi(state, homeId, zones[0].zoneId);
      dispatch(startDeviceServiceRequest(homeId, requestId, {}));

      zones
        .filter(zone => selectors.isInHolidayMode(state, homeId, zone.zoneId))
        .forEach(zone => {
          dispatch({
            type: types.SET_HOLIDAY_MODE_STARTED,
            homeId,
            zoneId: zone.zoneId,
            requestId,
            holidayMode: 0,
          });
        });

      const chZones = otherSelectors.getChZones(state, homeId);

      await dispatch(
        debounceSendControl(
          sendWsMessage,
          {
            action: isHpi ? 'hpiDesiredHolidayMode' : 'siDesiredHolidayMode',
            deviceToken,
            data: {
              [isHpi ? 'hpiId' : 'siId']: homeId,
              prtIds: zones.map(z => z.zoneId[0]),
              requestId,
              intoHoliday: false,
            },
          },
          {
            type: types.PENDING_CHANGE_TIMED_OUT,
            homeId,
            prtIds: chZones.map(z => z.zoneId[0]),
            requestId,
            suppressTimeoutError: true,
          },
          30000,
        ),
      );
    } else {
      await zones
        .filter(zone => selectors.isInHolidayMode(state, homeId, zone.zoneId))
        .map(async zone => {
          // if a DHW zone just fake the holiday cancel to make the PRT seem more responsive
          if (zone.zoneId[1] === 6) {
            dispatch({
              type: types.SET_HOLIDAY_MODE_STARTED,
              homeId,
              zoneId: zone.zoneId,
              requestId: uuid(),
              holidayMode: 0,
            });
          }
          // if a CH zone actually send the cancel holiday request, that will also cancel any DHW holiday modes
          else {
            const requestId = uuid();
            const isHpi = selectors.isHpi(state, homeId, zone.zoneId);

            dispatch({
              type: types.SET_HOLIDAY_MODE_STARTED,
              homeId,
              zoneId: zone.zoneId,
              requestId,
              holidayMode: 0,
            });

            const message = isHpi
              ? messages.hpiCancelHolidayMode(
                  homeId,
                  zone.zoneId,
                  requestId,
                  deviceToken,
                )
              : messages.cancelHolidayMode(
                  homeId,
                  zone.zoneId,
                  requestId,
                  deviceToken,
                );

            await dispatch(
              superDebounce(
                sendWsMessage,
                message,
                {
                  type: types.PENDING_CHANGE_TIMED_OUT,
                  homeId,
                  zoneId: zone.zoneId,
                  requestId,
                },
                {
                  holidayMode: 0,
                },
              ),
            );
          }
        });
    }
  };

export const requestPublish =
  (sendWsMessage, homeId, isHpi = false) =>
  async (dispatch, getState) => {
    const requestId = uuid();

    const state = getState();
    const deviceToken = otherSelectors.getDeviceToken(state);

    dispatch({
      type: types.WEBSOCKET_PUBLISH_STARTED,
      homeId,
      requestId,
    });

    const message = isHpi
      ? messages.hpiPublish(homeId, requestId, deviceToken)
      : messages.publish(homeId, requestId, deviceToken);

    await dispatch(
      debouncedSendAndTimeout(
        sendWsMessage,
        message,
        {
          type: 'Ignore this type',
          homeId,
          requestId,
        },
        5000,
      ),
    );
  };
